import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { reApi } from '../workspacesApi';
import { errorMessage } from '../errors';
import PickSelect from '../shared/PickSelect';
import OppContactField from './OppContactField';
import { RE_FIELDS, RE_CLOSED_STAGES, reFields, buildForm, createPayload, OppFields } from './oppFields';

const LABEL = 'text-sm font-medium leading-none';
const SIDE_OPTIONS = [{ value: 'buyer', label: 'Buyer' }, { value: 'seller', label: 'Seller' }];

// Create-opportunity modal for the RE flavor of the Board: same layout and contact search as the
// mortgage CreateOpportunityDialog, with the buyer or seller field set for the chosen side.
// `stagesBySide` = { buyer: [...], seller: [...] } from the board (terminal stages are not
// offered); `defaultSide` seeds the side from the Board's toggle; onCreated(opp, contact) lets
// the Board append the card without a refetch.
export default function CreateREOpportunityDialog({ open, onOpenChange, defaultSide, onCreated, stagesBySide = {} }) {
  const [contact, setContact] = useState(null);
  const [side, setSide] = useState(defaultSide);
  const [form, setForm] = useState(() => buildForm(RE_FIELDS));
  const [stage, setStage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSide(defaultSide);
  }, [open, defaultSide]);

  const stageOptions = useMemo(
    () => (stagesBySide[side] || []).filter(s => !RE_CLOSED_STAGES.includes(s.key)).map(s => ({ value: s.key, label: s.label })),
    [stagesBySide, side],
  );
  const stageValue = stageOptions.some(o => o.value === stage)
    ? stage
    : (stageOptions.find(o => o.value === 'new_lead') || stageOptions[0])?.value;
  const fields = useMemo(() => reFields(side), [side]);
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const create = async () => {
    if (!contact) { toast({ title: 'Select a contact' }); return; }
    setSaving(true);
    try {
      const opp = await reApi.createOpp({
        contact_id: contact.contact_id,
        side,
        ...createPayload(fields, form),
        ...(stageValue ? { stage: stageValue } : {}),
      });
      toast({ title: 'Opportunity created' });
      onCreated?.(opp, contact);
      setForm(buildForm(RE_FIELDS));
      setStage('');
      setContact(null);
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Create failed', description: errorMessage(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pipeline-create-dialog">
        <DialogHeader><DialogTitle className="font-serif" style={{ color: '#070B5F' }}>New Real Estate Opportunity</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <OppContactField contact={contact} onPick={setContact} />
          <div className="grid grid-cols-2 gap-3">
            <PickSelect label="Side" labelClassName={LABEL} value={side} onChange={setSide} options={SIDE_OPTIONS} testId="opp-side-select" />
            {stageOptions.length > 0 && (
              <PickSelect label="Stage" labelClassName={LABEL} value={stageValue} onChange={setStage} options={stageOptions} testId="opp-stage-select" />
            )}
          </div>
          <OppFields fields={fields} form={form} setField={setField} testPrefix="opp" />
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-outline" onClick={() => onOpenChange(false)}>Cancel</button>
            <button disabled={saving} onClick={create} data-testid="opp-save-btn" className="btn-gold">
              {saving && <Loader2 className="animate-spin" size={14} />} Create
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
